// 🔴 L2 (N°876) — FICHE CLIENT : ses SITES, et sous chaque site ses DOSSIERS.
// CLIENT 1 ─ N SITE 1 ─ N DOSSIER : un client peut avoir plusieurs sites, un
// site plusieurs dossiers successifs.
import { useCallback, useEffect, useState } from "react";
import { SiteForm } from "./SiteForm";
import { activiteDuTenant, creerDossierSurSite, creerSite, sitesDuClient } from "./api";
import { formatSiteAddress } from "../../../utils/clientFormat";

const CARTE = { background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
                borderRadius: 12, padding: 16 };

// 🔑 P-MARIO-01 — réutilisé par la FICHE DOSSIER : `siteCourantId` marque le lieu du dossier
//    affiché, `titre` nomme la section. Sans eux (fiche client), rien ne change.
export function SitesDuClient({ clientId, onOpenDossier, siteCourantId, titre = "Sites" }) {
  const [sites, setSites] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [ajout, setAjout] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const activite = activiteDuTenant();

  const charger = useCallback(async () => {
    try { setSites((await sitesDuClient(clientId)).sites || []); setErreur(null); }
    catch (e) { setErreur(e.message); setSites([]); }
  }, [clientId]);
  useEffect(() => { if (clientId) charger(); }, [clientId, charger]);

  const agir = async (fn) => {
    setOccupe(true); setErreur(null);
    try { await fn(); await charger(); } catch (e) { setErreur(e.message); }
    finally { setOccupe(false); }
  };

  return (
    <div style={CARTE} data-testid="client-sites">
      <div className="flex items-center justify-between mb-3">
        <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>{titre}</div>
        {!ajout && (
          <button type="button" data-testid="client-site-ajouter" onClick={() => setAjout(true)}
                  className="rounded-lg px-3 text-sm"
                  style={{ height: 36, background: "transparent",
                           border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)" }}>
            Ajouter un site
          </button>
        )}
      </div>
      {erreur && (
        <div className="text-sm mb-3 rounded-lg p-2" role="alert"
             style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)" }}>{erreur}</div>
      )}
      {ajout && (
        <div className="mb-3">
          <SiteForm occupe={occupe} onAnnuler={() => setAjout(false)}
                    onValider={(data) => agir(async () => { await creerSite(clientId, data); setAjout(false); })} />
        </div>
      )}
      {sites === null ? (
        <div className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>Chargement…</div>
      ) : !sites.length ? (
        <div className="text-sm" data-testid="client-sites-aucun" style={{ color: "var(--oh-text-secondary)" }}>
          Aucun site pour ce client. Un dossier concerne toujours un site : ajoutez-le d'abord.
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map((s) => (
            <div key={s.id} data-testid="client-site" data-courant={s.id === siteCourantId ? "oui" : undefined}
                 className="rounded-lg p-3"
                 style={{ border: `0.5px solid ${s.id === siteCourantId ? "var(--oh-accent)" : "var(--oh-border)"}`,
                          background: s.id === siteCourantId ? "var(--oh-accent-bg)" : "var(--oh-bg-surface)" }}>
              <div className="flex items-center gap-2 flex-wrap">
                <div style={{ color: "var(--oh-text)", fontWeight: 500 }}>{s.nom_site || s.adresse}</div>
                {s.id === siteCourantId && (
                  <span data-testid="client-site-courant" className="text-xs rounded-lg px-2"
                        style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)",
                                 border: "0.5px solid var(--oh-info-border)" }}>
                    Site de ce dossier
                  </span>
                )}
              </div>
              <div className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
                {/* N°929 — LE formateur commun (celui du bloc « site du dossier ») :
                    ⛔ plus de recomposition locale, qui doublait « 75006 Paris ». */}
                {formatSiteAddress(s)}
              </div>
              <div className="mt-2 space-y-1" data-testid="client-site-dossiers">
                {(s.dossiers || []).map((d) => (
                  <button key={d.id} type="button" onClick={() => onOpenDossier && onOpenDossier(d.id)}
                          className="text-sm underline block" data-testid="client-site-dossier"
                          style={{ color: "var(--oh-accent-text)", background: "none", border: "none", padding: 0 }}>
                    {d.reference || d.id}
                  </button>
                ))}
              </div>
              <button type="button" disabled={!activite || occupe} data-testid="client-site-nouveau-dossier"
                      onClick={() => agir(() => creerDossierSurSite(clientId, s.id, activite))}
                      className="mt-2 rounded-lg px-3 text-sm disabled:opacity-60"
                      style={{ height: 36, background: "transparent",
                               border: "0.5px solid var(--oh-border)", color: "var(--oh-text-secondary)" }}>
                Nouveau dossier sur ce site
              </button>
              {!activite && (
                <div className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }}>
                  L'activité des dossiers de ce métier n'est pas déclarée : aucune n'est choisie à sa place.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SitesDuClient;
